import { memo, useCallback, useMemo, useRef, useState } from 'react'
import type { Relation, Table } from '@/types'
import { calcPath, getOrthogonalPathLength, getPathTangentAtT, getPointAtT } from '@/utils/paths'
import { getRelationshipCardinalities, getRelationshipLabel } from '@/utils/relationships'
import { useDiagramStore } from '@/stores'
import { useViewportStore } from '@/stores/viewportStore'
import { CardinalityMark } from './CardinalityMark'

interface RelationPathProps {
  relation: Relation
  fromTable: Table
  toTable: Table
  allTables: Table[]
  color: string
  isSelected: boolean
  animationEnabled: boolean
  theme: 'light' | 'dark'
  onSelect: (relationId: string) => void
  readOnly?: boolean
}

export const RelationPath = memo(function RelationPath({
  relation,
  fromTable,
  toTable,
  allTables,
  color,
  isSelected,
  animationEnabled,
  theme,
  onSelect,
  readOnly = false,
}: RelationPathProps) {
  const [isHovered, setIsHovered] = useState(false)
  const updateRelationship = useDiagramStore((state) => state.updateRelationship)
  const labelDragRef = useRef<{ startT: number; startX: number; startY: number } | null>(null)

  const handleLabelPointerDown = useCallback((e: React.PointerEvent<SVGGElement>) => {
    if (readOnly) return
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    labelDragRef.current = {
      startT: relation.labelT ?? 0.5,
      startX: e.clientX,
      startY: e.clientY,
    }
  }, [readOnly, relation.labelT])

  const handleLabelPointerMove = useCallback((e: React.PointerEvent<SVGGElement>) => {
    if (!labelDragRef.current) return
    if (readOnly) return
    const sc = useViewportStore.getState().sc
    const dx = (e.clientX - labelDragRef.current.startX) / sc
    const dy = (e.clientY - labelDragRef.current.startY) / sc
    const currentPath = calcPath(relation, fromTable, toTable, allTables)
    const totalLen = getOrthogonalPathLength(currentPath.points)
    if (totalLen === 0) return
    const tangent = getPathTangentAtT(currentPath.points, labelDragRef.current.startT)
    const delta = dx * tangent.dx + dy * tangent.dy
    const newT = Math.max(0.1, Math.min(0.9, labelDragRef.current.startT + delta / totalLen))
    updateRelationship(relation.id, { labelT: newT })
  }, [readOnly, relation, fromTable, toTable, allTables, updateRelationship])

  const handleLabelPointerUp = useCallback(() => {
    labelDragRef.current = null
  }, [])

  // Manual routes don't use allTables for collision scoring — skip that dependency
  // so moving an unrelated table doesn't trigger recalculation for every manual relation.
  const tablesDep = relation.isManual ? null : allTables
  const path = useMemo(
    () => calcPath(relation, fromTable, toTable, allTables),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relation, fromTable, toTable, tablesDep],
  )

  const label = getRelationshipLabel(relation)

  const strokeWidth = isSelected || isHovered ? 2.2 : 1.35
  const strokeOpacity = isSelected || isHovered ? 1 : theme === 'dark' ? 0.82 : 0.72
  const labelWidth = label.length * 6.5 + 18
  const labelHeight = 18
  const labelAnchor = relation.labelT != null
    ? getPointAtT(path.points, relation.labelT)
    : path.midPoint
  const labelX = labelAnchor.x - labelWidth / 2
  const labelY = labelAnchor.y - 22
  const labelShadow = theme === 'dark'
    ? '0 12px 28px rgba(0,0,0,.76)'
    : '0 10px 22px rgba(15,23,42,.34)'
  const cardinality = getRelationshipCardinalities(relation)
  const animationClassName = animationEnabled
    ? relation.from === fromTable.id
      ? 'rpath-anim rpath-anim-reverse'
      : 'rpath-anim'
    : undefined

  return (
    <g data-relation-editor-affordance="true">
      <path
        d={path.d}
        fill="none"
        stroke="transparent"
        strokeWidth={12}
        pointerEvents="stroke"
        cursor={readOnly ? 'default' : 'pointer'}
        data-relation-editor-affordance="true"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(event) => {
          event.stopPropagation()
          onSelect(relation.id)
        }}
      />

      <path
        d={path.d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeOpacity={strokeOpacity}
        strokeDasharray="8 5"
        className={animationClassName}
        pointerEvents="none"
      />

      <g
        cursor={readOnly ? 'default' : 'grab'}
        onPointerDown={handleLabelPointerDown}
        onPointerMove={handleLabelPointerMove}
        onPointerUp={handleLabelPointerUp}
      >
        <rect
          x={labelX}
          y={labelY}
          width={labelWidth}
          height={labelHeight}
          rx={4}
          fill={theme === 'dark' ? 'rgba(2,8,18,.98)' : 'rgba(231,238,247,.98)'}
          fillOpacity={isSelected ? 0.98 : 0.94}
          stroke={color}
          strokeWidth={isSelected ? 1.2 : 1}
          style={{ filter: `drop-shadow(${labelShadow})` }}
        />
        <text
          x={labelAnchor.x}
          y={labelY + labelHeight / 2 + 0.5}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={8}
          fontFamily="Consolas, monospace"
          fontWeight={700}
          fill={isSelected ? color : theme === 'dark' ? '#A8C0D6' : '#475569'}
          pointerEvents="none"
        >
          {label}
        </text>
      </g>

      <CardinalityMark
        x={path.fromPoint.x}
        y={path.fromPoint.y}
        side={path.fromSide}
        type={cardinality.from}
        color={color}
      />
      <CardinalityMark
        x={path.toPoint.x}
        y={path.toPoint.y}
        side={path.toSide}
        type={cardinality.to}
        color={color}
      />
    </g>
  )
})
